import { z } from 'zod/v4';
import { QUESTION_RESPONSE_STATUSES } from './question-response-create.js';

/**
 * Path parameters of
 * `PATCH /api/questionnaires/requests/:requestId/questions/:questionId/response`.
 *
 * The legacy route was `PATCH /v1/responses/requests/:requestId/questions/:questionId`
 * — the same two ids in the same order, under a `/v1/responses` prefix that no
 * longer exists now that this is the last route on it. The answer is addressed as a
 * sub-resource of the request instead, matching the sibling
 * `POST /requests/:requestId/responses` that writes it.
 *
 * The 64-character caps are the `request_id` and `question_id` column widths. The
 * legacy params schema declared `minLength: 1` and no `required` array, which
 * Fastify does not enforce — moot either way, since both values come from the path.
 */
export const UpdateQuestionResponseStatusParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
  questionId: z.string().min(1).max(64),
});

export type UpdateQuestionResponseStatusParams = z.infer<
  typeof UpdateQuestionResponseStatusParamsSchema
>;

/**
 * Body of
 * `PATCH /api/questionnaires/requests/:requestId/questions/:questionId/response`.
 *
 * `status` is required, as legacy had it. The enum is
 * `QUESTION_RESPONSE_STATUSES`, reused from the sibling create contract rather
 * than redeclared — both are the legacy `RESPONSE_STATUS`
 * (`Object.keys(RESPONSE_STATUS_ENUM)`), and two copies of the same four members
 * could drift apart while the reviewer and responder paths must agree on them.
 *
 * Only `APPROVED` and `DENIED` are reachable from a button today: the old frontend
 * hard-coded one per wrapper, and frontend-v2's `handleQuestionStatusChange` is
 * typed to those two. `IN_REVIEW` and `NOT_SUBMITTED` stay accepted because legacy
 * accepted them and an API-key client may be sending either; narrowing the enum
 * would be a silent 400 on a call that succeeds today.
 *
 * The legacy JSON Schema set no `additionalProperties: false`, so unknown keys were
 * accepted and ignored; zod's default object strips them, which is the same
 * observable behaviour.
 */
export const UpdateQuestionResponseStatusBodySchema = z.object({
  status: z.enum(QUESTION_RESPONSE_STATUSES),
});

export type UpdateQuestionResponseStatusBody = z.infer<
  typeof UpdateQuestionResponseStatusBodySchema
>;
